import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";
import { LogOut, Lock, Pencil, Trash2, Check, X, Download } from "lucide-react";

import { API } from "@/lib/api";
const TOKEN_KEY = "wedding_admin_token";

const StatusBadge = ({ ok, yes, no }) => (
  <span
    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] tracking-[0.15em] uppercase ${
      ok ? "bg-olive/15 text-olive" : "bg-wine/10 text-wine"
    }`}
  >
    <span className={`w-1.5 h-1.5 rounded-full ${ok ? "bg-olive" : "bg-wine"}`} />
    {ok ? yes : no}
  </span>
);

const MenusCell = ({ menus, t }) => {
  if (!menus) return <span>—</span>;
  const items = [
    { k: "lubina", label: "Lubina", v: menus.lubina },
    { k: "iberico", label: "Ibérico", v: menus.iberico },
    { k: "menu_14_17", label: "14-17", v: menus.menu_14_17 },
    { k: "menu_6_13", label: "6-13", v: menus.menu_6_13 },
    { k: "menu_2_5", label: "2-5", v: menus.menu_2_5 },
  ].filter((x) => (x.v || 0) > 0);
  if (items.length === 0) return <span className="text-olive/50">—</span>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((it) => (
        <span
          key={it.k}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-wine/8 border border-wine/15 text-[10px] tracking-[0.05em] text-wine"
        >
          <span className="font-serif text-sm">{it.v}</span>
          <span className="text-[9px] uppercase tracking-[0.12em] text-olive">{it.label}</span>
        </span>
      ))}
    </div>
  );
};

const AdminPage = () => {
  const { t } = useLang();
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [pwd, setPwd] = useState("");
  const [rsvps, setRsvps] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // RSVP being edited
  const [confirmDelete, setConfirmDelete] = useState(null); // RSVP being deleted

  const headers = { "x-admin-token": token };

  const fetchData = async (tk) => {
    setLoading(true);
    try {
      const [rRes, sRes] = await Promise.all([
        axios.get(`${API}/admin/rsvps`, { headers: { "x-admin-token": tk } }),
        axios.get(`${API}/admin/stats`, { headers: { "x-admin-token": tk } }),
      ]);
      setRsvps(rRes.data);
      setStats(sRes.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        toast.error(t.admin.wrong);
      } else {
        toast.error("Error");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchData(token);
    // eslint-disable-next-line
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password: pwd });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setPwd("");
    } catch (err) {
      toast.error(t.admin.wrong);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setRsvps([]);
    setStats(null);
  };

  const saveEdit = async () => {
    if (editing.attending) {
      const m = editing.menus || { lubina: 0, iberico: 0, menu_14_17: 0, menu_6_13: 0, menu_2_5: 0 };
      const total = Math.max(1, Number(editing.total_attendees) || 1);
      const sum = (m.lubina || 0) + (m.iberico || 0) + (m.menu_14_17 || 0) + (m.menu_6_13 || 0) + (m.menu_2_5 || 0);
      if (sum !== total) {
        toast.error(t.rsvp.assignAll);
        return;
      }
    }
    try {
      await axios.patch(
        `${API}/admin/rsvps/${editing.id}`,
        {
          name: editing.name,
          attending: editing.attending,
          menus: editing.attending ? (editing.menus || { lubina: 0, iberico: 0, menu_14_17: 0, menu_6_13: 0, menu_2_5: 0 }) : { lubina: 0, iberico: 0, menu_14_17: 0, menu_6_13: 0, menu_2_5: 0 },
          total_attendees: Math.max(1, Number(editing.total_attendees) || 1),
          allergies: editing.allergies || "",
          message: editing.message || "",
        },
        { headers }
      );
      toast.success(t.admin.savedOk);
      setEditing(null);
      fetchData(token);
    } catch (err) {
      toast.error("Error");
    }
  };

  const doDelete = async () => {
    try {
      await axios.delete(`${API}/admin/rsvps/${confirmDelete.id}`, { headers });
      toast.success(t.admin.deletedOk);
      setConfirmDelete(null);
      fetchData(token);
    } catch (err) {
      toast.error("Error");
    }
  };

  const downloadCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/rsvps/export.csv`, {
        headers,
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const today = new Date().toISOString().slice(0, 10);
      a.download = `rsvps_marcel_aida_${today}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t.admin.csvDownloaded);
    } catch (err) {
      toast.error("Error");
    }
  };

  if (!token) {
    return (
      <main className="min-h-screen flex items-center justify-center px-6 parchment-bg">
        <div className="w-full max-w-sm bg-white rounded-2xl p-10 border border-olive/10 shadow-[0_8px_30px_rgb(107,45,49,0.06)]">
          <div className="w-12 h-12 rounded-full bg-wine/10 mx-auto flex items-center justify-center mb-6">
            <Lock className="w-5 h-5 text-wine" strokeWidth={1.5} />
          </div>
          <h1 className="font-serif text-2xl text-wine text-center mb-8">{t.admin.loginTitle}</h1>
          <form onSubmit={login} className="space-y-6">
            <input
              data-testid="admin-password-input"
              type="password"
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              placeholder={t.admin.passwordPlaceholder}
              autoFocus
              className="w-full bg-transparent border-b border-olive/25 px-1 py-3 text-base focus:outline-none focus:border-wine transition-colors placeholder:text-olive/50"
            />
            <button
              data-testid="admin-login-btn"
              type="submit"
              className="w-full bg-wine text-cream py-3.5 rounded-full text-[11px] tracking-[0.3em] uppercase transition-all duration-500 hover:bg-wine-deep hover:scale-[1.01]"
            >
              {t.admin.enter}
            </button>
          </form>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="admin-dashboard" className="min-h-screen parchment-bg pb-24">
      <header className="border-b border-olive/10 bg-cream/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between gap-4">
          <h1 className="font-serif text-lg md:text-xl text-wine">{t.admin.title}</h1>
          <div className="flex items-center gap-2 md:gap-5">
            <button
              data-testid="admin-csv-btn"
              onClick={downloadCsv}
              disabled={rsvps.length === 0}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-wine/30 text-wine text-[10px] md:text-[11px] tracking-[0.25em] uppercase hover:bg-wine hover:text-cream transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-wine"
            >
              <Download className="w-3.5 h-3.5" strokeWidth={1.6} />
              <span className="hidden sm:inline">{t.admin.downloadCsv}</span>
              <span className="sm:hidden">CSV</span>
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-olive hover:text-wine transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} />
              <span className="hidden sm:inline">{t.admin.logout}</span>
            </button>
          </div>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <StatCard label={t.admin.total} value={stats.total} />
            <StatCard label={t.admin.attending} value={stats.attending} accent />
            <StatCard label={t.admin.notAttending} value={stats.not_attending} />
            <StatCard label={t.admin.totalGuests} value={stats.total_guests} accent />
          </div>
        )}
        {stats && stats.menus && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
            <StatCard label={t.rsvp.menuLubina} value={stats.menus.lubina || 0} />
            <StatCard label={t.rsvp.menuIberico} value={stats.menus.iberico || 0} />
            <StatCard label={t.rsvp.menuTeen1417} value={stats.menus.menu_14_17 || 0} />
            <StatCard label={t.rsvp.menuChild613} value={stats.menus.menu_6_13 || 0} />
            <StatCard label={t.rsvp.menuChild25} value={stats.menus.menu_2_5 || 0} />
          </div>
        )}

        <div className="bg-white rounded-2xl border border-olive/10 shadow-[0_8px_30px_rgba(0,0,0,0.35)] overflow-hidden">
          {loading ? (
            <div className="p-16 text-center text-olive font-editorial italic">…</div>
          ) : rsvps.length === 0 ? (
            <div data-testid="admin-empty" className="p-16 text-center font-editorial italic text-olive">{t.admin.empty}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-rsvp-table">
                <thead>
                  <tr className="text-[10px] tracking-[0.25em] uppercase text-olive border-b border-olive/10">
                    <th className="px-6 py-4 text-left">{t.admin.name}</th>
                    <th className="px-6 py-4 text-left">{t.admin.status}</th>
                    <th className="px-6 py-4 text-left">{t.admin.menu}</th>
                    <th className="px-6 py-4 text-left">{t.admin.companionsCol}</th>
                    <th className="px-6 py-4 text-left">{t.admin.allergiesCol}</th>
                    <th className="px-6 py-4 text-left">{t.admin.messageCol}</th>
                    <th className="px-6 py-4 text-left">{t.admin.date}</th>
                    <th className="px-6 py-4 text-right">{t.admin.actions}</th>
                  </tr>
                </thead>
                <tbody>
                  {rsvps.map((r) => (
                    <tr key={r.id} className="border-b border-olive/5 hover:bg-cream/40 transition-colors" data-testid={`rsvp-row-${r.id}`}>
                      <td className="px-6 py-4 font-serif text-base text-wine">{r.name}</td>
                      <td className="px-6 py-4">
                        <StatusBadge ok={r.attending} yes={t.rsvp.yes} no={t.rsvp.no} />
                      </td>
                      <td className="px-6 py-4">{r.attending ? <MenusCell menus={r.menus} t={t} /> : <span className="text-olive/50">—</span>}</td>
                      <td className="px-6 py-4 text-charcoal font-medium">{r.attending ? (r.total_attendees || 1) : "—"}</td>
                      <td className="px-6 py-4 text-charcoal max-w-[200px] truncate" title={r.allergies}>{r.allergies || "—"}</td>
                      <td className="px-6 py-4 text-charcoal italic font-editorial max-w-[260px] truncate" title={r.message}>{r.message || "—"}</td>
                      <td className="px-6 py-4 text-xs text-olive whitespace-nowrap">
                        {new Date(r.created_at).toLocaleDateString()} {new Date(r.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            data-testid={`edit-${r.id}`}
                            onClick={() => setEditing({ ...r, total_attendees: r.total_attendees || 1 })}
                            className="w-8 h-8 rounded-full border border-olive/30 text-olive hover:bg-olive hover:text-cream hover:border-olive transition-all flex items-center justify-center"
                            title={t.admin.edit}
                          >
                            <Pencil className="w-3.5 h-3.5" strokeWidth={1.8} />
                          </button>
                          <button
                            data-testid={`delete-${r.id}`}
                            onClick={() => setConfirmDelete(r)}
                            className="w-8 h-8 rounded-full border border-wine/30 text-wine hover:bg-wine hover:text-cream hover:border-wine transition-all flex items-center justify-center"
                            title={t.admin.delete}
                          >
                            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.8} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {editing && (
        <EditModal
          rsvp={editing}
          setRsvp={setEditing}
          onClose={() => setEditing(null)}
          onSave={saveEdit}
          t={t}
        />
      )}

      {confirmDelete && (
        <ConfirmModal
          title={t.admin.confirmDeleteTitle}
          message={`${t.admin.confirmDeleteMsg} "${confirmDelete.name}"?`}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={doDelete}
          confirmLabel={t.admin.delete}
          cancelLabel={t.admin.cancel}
        />
      )}
    </main>
  );
};

const StatCard = ({ label, value, accent }) => (
  <div className={`bg-white rounded-2xl px-6 py-7 border ${accent ? "border-wine/30" : "border-olive/10"}`}>
    <div className="text-[10px] tracking-[0.3em] uppercase text-olive mb-2">{label}</div>
    <div className={`font-serif text-4xl ${accent ? "text-wine" : "text-charcoal"}`}>{value}</div>
  </div>
);

const EditModal = ({ rsvp, setRsvp, onClose, onSave, t }) => {
  const upd = (k, v) => setRsvp((r) => ({ ...r, [k]: v }));
  return (
    <div className="fixed inset-0 z-50 bg-charcoal/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        data-testid="edit-modal"
        className="w-full max-w-lg bg-white rounded-2xl p-8 shadow-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-serif text-2xl text-wine">{t.admin.editTitle}</h3>
          <button onClick={onClose} className="text-olive hover:text-wine">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-5">
          <Field label={t.rsvp.name}>
            <input
              data-testid="edit-name"
              value={rsvp.name}
              onChange={(e) => upd("name", e.target.value)}
              className="w-full bg-transparent border-b border-olive/25 px-1 py-2 focus:outline-none focus:border-wine"
            />
          </Field>

          <Field label={t.rsvp.attending}>
            <div className="flex gap-2 pt-1">
              <Toggle active={rsvp.attending === true} onClick={() => upd("attending", true)}>
                {t.rsvp.yes}
              </Toggle>
              <Toggle active={rsvp.attending === false} onClick={() => upd("attending", false)}>
                {t.rsvp.no}
              </Toggle>
            </div>
          </Field>

          {rsvp.attending && (
            <>
              <Field label={t.rsvp.companions}>
                <input
                  data-testid="edit-attendees"
                  type="number"
                  min="1"
                  max="30"
                  value={rsvp.total_attendees}
                  onChange={(e) => upd("total_attendees", e.target.value)}
                  className="w-full bg-transparent border-b border-olive/25 px-1 py-2 focus:outline-none focus:border-wine"
                />
              </Field>

              <Field label={t.rsvp.menuChoice}>
                <EditMenus rsvp={rsvp} upd={upd} t={t} />
              </Field>

              <Field label={t.rsvp.allergies}>
                <input
                  data-testid="edit-allergies"
                  value={rsvp.allergies || ""}
                  onChange={(e) => upd("allergies", e.target.value)}
                  className="w-full bg-transparent border-b border-olive/25 px-1 py-2 focus:outline-none focus:border-wine"
                />
              </Field>
            </>
          )}

          <Field label={t.rsvp.message}>
            <textarea
              data-testid="edit-message"
              rows="3"
              value={rsvp.message || ""}
              onChange={(e) => upd("message", e.target.value)}
              className="w-full bg-transparent border-b border-olive/25 px-1 py-2 focus:outline-none focus:border-wine resize-none"
            />
          </Field>
        </div>

        <div className="flex gap-3 mt-8">
          <button
            onClick={onClose}
            className="flex-1 border border-olive/30 text-olive py-3 rounded-full text-[11px] tracking-[0.25em] uppercase hover:bg-olive/10 transition-colors"
          >
            {t.admin.cancel}
          </button>
          <button
            data-testid="edit-save-btn"
            onClick={onSave}
            className="flex-1 bg-wine text-cream py-3 rounded-full text-[11px] tracking-[0.25em] uppercase hover:bg-wine-deep transition-colors inline-flex items-center justify-center gap-2"
          >
            <Check className="w-3.5 h-3.5" />
            {t.admin.save}
          </button>
        </div>
      </div>
    </div>
  );
};

const ConfirmModal = ({ title, message, onCancel, onConfirm, confirmLabel, cancelLabel }) => (
  <div className="fixed inset-0 z-50 bg-charcoal/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onCancel}>
    <div
      data-testid="confirm-modal"
      className="w-full max-w-md bg-white rounded-2xl p-8 shadow-2xl"
      onClick={(e) => e.stopPropagation()}
    >
      <h3 className="font-serif text-2xl text-wine mb-3">{title}</h3>
      <p className="font-editorial italic text-charcoal mb-8">{message}</p>
      <div className="flex gap-3">
        <button
          onClick={onCancel}
          className="flex-1 border border-olive/30 text-olive py-3 rounded-full text-[11px] tracking-[0.25em] uppercase hover:bg-olive/10 transition-colors"
        >
          {cancelLabel}
        </button>
        <button
          data-testid="confirm-delete-btn"
          onClick={onConfirm}
          className="flex-1 bg-wine text-cream py-3 rounded-full text-[11px] tracking-[0.25em] uppercase hover:bg-wine-deep transition-colors"
        >
          {confirmLabel}
        </button>
      </div>
    </div>
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <label className="block text-[10px] tracking-[0.3em] uppercase text-olive mb-1">{label}</label>
    {children}
  </div>
);

const Toggle = ({ active, onClick, children, small }) => (
  <button
    type="button"
    onClick={onClick}
    className={`${small ? "px-3 py-2 text-[10px]" : "flex-1 px-4 py-2.5 text-[11px]"} rounded-full tracking-[0.15em] uppercase transition-all border ${
      active
        ? "bg-wine text-cream border-wine"
        : "bg-transparent text-olive border-olive/25 hover:border-wine hover:text-wine"
    }`}
  >
    {children}
  </button>
);

const EditMenus = ({ rsvp, upd, t }) => {
  const menus = rsvp.menus || { lubina: 0, iberico: 0, menu_14_17: 0, menu_6_13: 0, menu_2_5: 0 };
  const total = Math.max(1, Number(rsvp.total_attendees) || 1);
  const assigned = (menus.lubina || 0) + (menus.iberico || 0) + (menus.menu_14_17 || 0) + (menus.menu_6_13 || 0) + (menus.menu_2_5 || 0);
  const remaining = total - assigned;
  const over = assigned > total;

  const change = (key, delta) => {
    const current = menus[key] || 0;
    const next = current + delta;
    if (next < 0) return;
    const newAssigned = (key === "lubina" ? next : menus.lubina || 0)
      + (key === "iberico" ? next : menus.iberico || 0)
      + (key === "menu_14_17" ? next : menus.menu_14_17 || 0)
      + (key === "menu_6_13" ? next : menus.menu_6_13 || 0)
      + (key === "menu_2_5" ? next : menus.menu_2_5 || 0);
    if (delta > 0 && newAssigned > total) return;
    upd("menus", { ...menus, [key]: next });
  };

  const rows = [
    { k: "lubina", label: t.rsvp.menuLubina },
    { k: "iberico", label: t.rsvp.menuIberico },
    { k: "menu_14_17", label: t.rsvp.menuTeen1417 },
    { k: "menu_6_13", label: t.rsvp.menuChild613 },
    { k: "menu_2_5", label: t.rsvp.menuChild25 },
  ];

  return (
    <div className="space-y-2 pt-2">
      {rows.map((r) => (
        <div key={r.k} className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-cream/60 border border-olive/15" data-testid={`edit-menu-${r.k}`}>
          <span className="font-editorial text-sm text-charcoal flex-1">{r.label}</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => change(r.k, -1)}
              disabled={(menus[r.k] || 0) <= 0}
              data-testid={`edit-menu-${r.k}-dec`}
              className="w-8 h-8 rounded-full border border-olive/30 text-olive flex items-center justify-center hover:border-wine hover:text-wine disabled:opacity-30 disabled:hover:border-olive/30 disabled:hover:text-olive transition-all"
            >−</button>
            <span data-testid={`edit-menu-${r.k}-value`} className="font-serif text-lg text-wine w-6 text-center">{menus[r.k] || 0}</span>
            <button
              type="button"
              onClick={() => change(r.k, +1)}
              disabled={assigned >= total}
              data-testid={`edit-menu-${r.k}-inc`}
              className="w-8 h-8 rounded-full border border-wine/40 text-wine flex items-center justify-center hover:bg-wine hover:text-cream disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-wine transition-all"
            >+</button>
          </div>
        </div>
      ))}
      <div className={`text-[10px] tracking-[0.18em] uppercase mt-2 ${over ? "text-wine" : remaining === 0 ? "text-olive" : "text-olive/80"}`}>
        {over ? t.rsvp.overAssigned : remaining > 0 ? `${t.rsvp.remaining}: ${remaining} / ${total}` : `${assigned} / ${total}`}
      </div>
    </div>
  );
};

export default AdminPage;
